"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  ArticleHistory,
  WikiArticle,
  StoredArticleSummary,
  MILESTONES,
} from "@/lib/wikipedia";
import * as historyApi from "@/lib/historyApi";
import { useOCAccount } from "@/hooks/useOCAccount";

const defaultHistory: ArticleHistory = {
  articles: [],
  totalRead: 0,
  milestonesAchieved: [],
  lastArticle: undefined,
};

export function useArticleHistory() {
  const [history, setHistory] = useState<ArticleHistory>(defaultHistory);
  const [isLoaded, setIsLoaded] = useState(false);
  const [newMilestone, setNewMilestone] = useState<
    (typeof MILESTONES)[number] | null
  >(null);
  const { isLoggedIn, ocId } = useOCAccount();

  // Track when current article started being read
  const articleStartTime = useRef<Date | null>(null);
  const currentArticle = useRef<WikiArticle | null>(null);

  /**
   * Convert StoredArticleSummary to WikiArticle format
   */
  const toWikiArticle = (article: StoredArticleSummary): WikiArticle => ({
    title: article.title,
    extract: "", // Will be refetched if needed
    fullUrl: article.url,
    pageId: article.pageId,
    categories: article.categories,
    thumbnail: article.thumbnail,
    description: article.description,
    timestamp: article.startedAt || new Date().toISOString(),
  });

  // Load history from API when authenticated
  useEffect(() => {
    if (!isLoggedIn || !ocId) {
      setIsLoaded(true);
      return;
    }

    async function loadHistory() {
      try {
        const data = await historyApi.fetchHistory();
        // Convert lastArticle from StoredArticleSummary to WikiArticle if needed
        let lastArticle: WikiArticle | undefined;
        if (data.lastArticle) {
          // API returns StoredArticleSummary format with 'url' field
          // We need to convert to WikiArticle format with 'fullUrl' field
          const la = data.lastArticle as unknown as Record<string, unknown>;
          if ("url" in la && !("fullUrl" in la)) {
            lastArticle = toWikiArticle(
              data.lastArticle as unknown as StoredArticleSummary,
            );
          } else {
            lastArticle = data.lastArticle as WikiArticle;
          }
        }

        setHistory({
          articles: data.articles,
          totalRead: data.totalRead,
          milestonesAchieved: data.milestonesAchieved,
          lastArticle,
        });
      } catch (error) {
        console.error("Failed to load history from API:", error);
      } finally {
        setIsLoaded(true);
      }
    }

    loadHistory();
  }, [isLoggedIn, ocId]);

  /**
   * Start tracking reading time for an article
   * Call this when a new article is displayed
   * Accepts both WikiArticle (fullUrl) and StoredArticleSummary (url)
   */
  const startReading = useCallback(
    async (article: WikiArticle | StoredArticleSummary) => {
      articleStartTime.current = new Date();
      // Convert StoredArticleSummary to WikiArticle format for currentArticle ref
      const wikiArticle: WikiArticle =
        "fullUrl" in article
          ? article
          : {
              ...article,
              fullUrl: article.url,
              extract: "",
              timestamp: article.startedAt || new Date().toISOString(),
            };
      currentArticle.current = wikiArticle;

      // Update local state immediately for responsive UI
      setHistory((prev) => ({
        ...prev,
        lastArticle: wikiArticle,
      }));

      // Get the URL from either format
      const articleUrl = "fullUrl" in article ? article.fullUrl : article.url;

      // Sync with backend if authenticated
      if (isLoggedIn && articleUrl) {
        try {
          await historyApi.startReading({
            pageId: article.pageId,
            title: article.title,
            url: articleUrl,
            description: article.description,
            categories: article.categories,
            thumbnail: article.thumbnail,
          });
        } catch (error) {
          console.error("Failed to sync startReading with API:", error);
        }
      }
    },
    [isLoggedIn],
  );

  /**
   * Mark current article as read with reading time tracked
   */
  const markAsRead = useCallback(async () => {
    if (!currentArticle.current || !articleStartTime.current) {
      return;
    }

    const article = currentArticle.current;
    const startedAt = articleStartTime.current;
    const readAt = new Date();
    const timeSpentSeconds = Math.round(
      (readAt.getTime() - startedAt.getTime()) / 1000,
    );

    // Create stored summary with metadata
    const summary: StoredArticleSummary = {
      title: article.title,
      url: article.fullUrl,
      pageId: article.pageId,
      description: article.description,
      categories: article.categories,
      thumbnail: article.thumbnail,
      readAt: readAt.toISOString(),
      startedAt: startedAt.toISOString(),
      timeSpentSeconds,
    };

    // Sync with backend if authenticated
    let newMilestones: number[] = [];
    if (isLoggedIn) {
      try {
        const response = await historyApi.markAsRead({
          pageId: article.pageId,
          timeSpentSeconds,
        });
        newMilestones = response.newMilestones;
      } catch (error) {
        console.error("Failed to sync markAsRead with API:", error);
      }
    }

    // Update local state
    setHistory((prev) => {
      const newTotalRead = prev.totalRead + 1;

      // Check for new milestone from API response or local check
      let newlyAchieved = MILESTONES.find((m) => newMilestones.includes(m.id));

      // Fallback to local check if API didn't return milestones
      if (!newlyAchieved) {
        newlyAchieved = MILESTONES.find(
          (m) =>
            m.count === newTotalRead && !prev.milestonesAchieved.includes(m.id),
        );
      }

      if (newlyAchieved) {
        setNewMilestone(newlyAchieved);
      }

      return {
        articles: [...prev.articles.slice(-49), summary], // Keep last 50 article summaries
        totalRead: newTotalRead,
        milestonesAchieved: newlyAchieved
          ? [...prev.milestonesAchieved, newlyAchieved.id]
          : prev.milestonesAchieved,
        lastArticle: article,
      };
    });

    // Reset tracking
    articleStartTime.current = null;
    currentArticle.current = null;
  }, [isLoggedIn]);

  const clearNewMilestone = useCallback(() => {
    setNewMilestone(null);
  }, []);

  const resetHistory = useCallback(() => {
    setHistory(defaultHistory);
    articleStartTime.current = null;
    currentArticle.current = null;
  }, []);

  /**
   * Get total reading time across all articles
   */
  const getTotalReadingTime = useCallback(() => {
    return history.articles.reduce(
      (total, article) => total + article.timeSpentSeconds,
      0,
    );
  }, [history.articles]);

  return {
    history,
    isLoaded,
    lastArticle: history.lastArticle,
    startReading,
    markAsRead,
    newMilestone,
    clearNewMilestone,
    resetHistory,
    getTotalReadingTime,
  };
}
